import { createContext, Fragment, ReactElement, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { GlassButton } from "./GlassButtons";
import { Localization } from "@viamap/viamap2-common";
import { autoUpdate, flip, FloatingFocusManager, FloatingList, offset, size, useClick, useDismiss, useFloating, useHover, useInteractions, useListItem, useListNavigation, useRole, useTypeahead } from "@floating-ui/react";
import { ColorPicker } from "./ComponentUtils";
import { Reorder } from "framer-motion";
import { Typeahead } from "react-bootstrap-typeahead";
import { MIBIcon } from "src/componentsUtils/MIBIcon";
import { MITFloatingPortal } from "src/componentsUtils/PortalHelpers";
import React from "react";
import { LayerHierarchy, LayerInfo, LayerType } from "src/common/managers/Types";
import { getAllGroupsAndDecendedLayers } from "src/managers/OldHierarchy";



export function GlassInputGroupTitle(props: { children }) {
   return (
      <div className="GlassInputGroupTitle">{props.children}</div>
   )
}

export function GlassInputGroupEscape(props: { children }) {
   return (
      <div className="GlassInputGroupEscape">{props.children}</div>
   )
}

export function GlassInputGroup(props: { children, style?, autoFocus?: boolean | number, onSubmit?: (e: any) => void, onEscape?: (e: any) => void }) {
   const ref = useRef<HTMLFormElement>(null)

   useEffect(() => {
      if (typeof props.autoFocus === "number") {
         ref.current?.parentElement?.querySelectorAll(':is(input, select, checkbox)').forEach((a, idx) => {
            if (idx === props.autoFocus && a instanceof HTMLElement) {
               a.focus()
            }
         })
      } else if (props.autoFocus) {
         ref.current?.parentElement?.querySelectorAll(':is(input, select, checkbox)').forEach((a, idx) => {
            if (idx === props.autoFocus && a instanceof HTMLElement) {
               a.focus()
            }
         })
      }
   }, [])

   return (
      <form className="GlassInputGroup" ref={ref} style={props.style}
         onSubmit={(e) => { e.preventDefault(); props.onSubmit?.(e) }}
         onKeyDown={(e) => {
            if (e.key === "Escape") { props.onEscape?.(e) }
            if (e.key === "Enter") {
               if (document.activeElement instanceof HTMLTextAreaElement) return


               e.preventDefault();
               let focusInput = false
               let shouldSubmit = true
               ref.current?.parentElement?.querySelectorAll(':is(input, select, checkbox)').forEach((a) => {
                  if (a instanceof HTMLElement && focusInput && shouldSubmit) {
                     a.focus()
                     shouldSubmit = false
                  }
                  if (a === document.activeElement) {
                     focusInput = true
                  }
               })
               if (shouldSubmit) {
                  props.onSubmit?.(e)
               }
            }

         }}
      >
         {props.children}
      </form>
   )
}

type ColorPalleteProps = {
   colorSet: string[],
   defaultColor: string,
   getter: () => string,
   onChange: (color: string) => void
}
export function GlassColorPalette({
   colorSet,
   defaultColor,
   getter,
   onChange
}: ColorPalleteProps) {
   const [showColorPicker, setShowColorPicker] = useState<boolean>(false)
   const { refs, floatingStyles } = useFloating({
      whileElementsMounted: autoUpdate,
      placement: "left",
   });


   return (
      <>
         <div className="GlassColorPalette">
            {colorSet.includes(getter()) ? colorSet.map((color, idx) =>
               idx === 0 && colorSet.includes(color, 1) ? null :
                  <Fragment key={color}><GlassColorBox selected={color === getter()} onClick={() => onChange(color)} color={color} /></Fragment>
            )
               :
               <>
                  <GlassColorBox selected={true} color={getter()} />
                  <GlassButton
                     onClick={() => { onChange(defaultColor); }}
                  >
                     {Localization.getText("Restore default")}
                  </GlassButton>
               </>
            }
            <div className="Cog" style={{ color: getter() }} ref={refs.setReference} onClick={() => setShowColorPicker(true)} title={Localization.getText("Click to edit color")} >
               <MIBIcon icon="selectColor" />
            </div>
         </div>
         {showColorPicker ? (
            <MITFloatingPortal >
               <div ref={refs.setFloating} style={{ ...floatingStyles, zIndex: "1001" }} >
                  <ColorPicker
                     color={getter() || "blue"}
                     onCommit={(color) => {
                        onChange(color)
                        setShowColorPicker(false)
                     }}
                     onCancel={() => setShowColorPicker(false)}
                  />
               </div>
            </MITFloatingPortal>
         ) : null}
      </>
   )
}

export function GlassColorBox(props: { color: string, selected?: boolean, onClick?: (e: any) => void }) {
   return (
      <div onClick={props.onClick} className={"GlassColorBox" + (props.selected ? " selected" : "")} style={({ "--color": props.color } as any)} ></div>
   )
}

export function GlassTextInput(
) {

   return (
      <>
         <div className="GlassInputLabel"></div>
         <div className="GlassInputField"></div>
      </>
   )
}

export function GlassTextInputTransformer(props: { label, children, top?: string }) {

   return (
      <>
         <div className={"GlassInputLabel " + (props.top ? "top" : "")} style={{
            marginTop: props.top || "0px"
         }} >{props.label}</div>
         <div className="GlassInputField">{props.children}</div>
      </>
   )
}

export function GlassInputControl(props: { children }) {
   return (
      <div className="GlassInputControl">{props.children}</div>
   )
}

export function GlassInputStack(props: { children }) {
   return (
      <div className="GlassInputStack">{props.children}</div>
   )
}

export function GlassInputControlLabel(props: { children, top?: boolean }) {
   return (
      <div className={"GlassInputLabel" + (props.top ? " top" : "")}>{props.children}</div>
   )
}
/**
 * Glass Input Control Button with tooltip GICButton
 */
export function GICButton({ children, title, ...props }: { children, title?: string } & React.ButtonHTMLAttributes<HTMLButtonElement>) {
   const [tooltipOpen, setTooltipOpen] = useState(false);
   const { refs, floatingStyles, context: tooltipContext } = useFloating({
      whileElementsMounted: autoUpdate,
      placement: props["title-placement"],
      open: tooltipOpen,
      middleware: [offset(5)],
      onOpenChange: setTooltipOpen,
   });

   const {
      getFloatingProps: getTooltipFloatingProps
   } = useInteractions([useHover(tooltipContext), useDismiss(tooltipContext)]);


   return (
      <>
         <button {...props} ref={refs.setReference} className="GICButton" type="button" >
            {children}
         </button>
         {Boolean(title && tooltipOpen) &&
            <MITFloatingPortal>
               <div ref={refs.setFloating} className="GlassButtonToolTip" style={floatingStyles} {...getTooltipFloatingProps()} >
                  {title || "ThisIsToolTIp"}
               </div>
            </MITFloatingPortal>
         }
      </>
   )
}



export function GlassTypeAheadReordering(props: {
   layerId: number,
   id?: string,
   options?: { label, value }[],
   selected?: { label, value }[],
   placeholder?: string,
   emptyLabel?: string,
   onChange?: (a: any[]) => void,
   settingComponent?: React.ReactNode,
}) {

   const [mode, setMode] = useState<"default" | "reorder">("default")

   return (
      <div style={{ width: "100%" }}>
         <div style={{ display: "flex" }}>
            {/* Typeahead */}
            <div style={{ flex: 1 }}>
               <Typeahead
                  multiple={true}
                  id={props.id}
                  emptyLabel={props.emptyLabel}
                  placeholder={props.placeholder}
                  className={"GlassReorderTypeahead " + (mode === "reorder" ? " reorder" : "default")}
                  options={props.options || []}
                  selected={props.selected || []}
                  onChange={(a) => props.onChange?.(a)}
                  renderToken={(option, rTprops, index) => {
                     if ((index !== 0 && mode == "reorder")) return <></>
                     if ((mode == "default")) {
                        return (
                           <Fragment key={index}>
                              <div className={"rbt-token rbt-token-removeable"} >
                                 <div className="rbt-token-label" >{option["label"]}</div><button className="close btn-close rbt-close rbt-token-remove-button" onClick={() => rTprops.onRemove?.(option)}>×</button>
                              </div>
                           </Fragment>
                        )
                     }
                     if (index !== 0) { return <></> }
                     return (
                        <Reorder.Group values={props.selected?.map(a => a.value) || []} className="rbt-input-wrapper" as="div" axis="y" animated={false} onReorder={(a) => props.onChange?.(a.map((a) => props.options?.find((b) => b.value === a) || { label: a, value: a }))} >
                           {(props.selected || []).map((option) => {
                              return (<Reorder.Item as="div" dragListener={mode == "reorder"} className={"rbt-token rbt-token-removeable reOrder"} value={option.value} key={option.label} >
                                 <div className="rbt-token-label" onMouseDown={(e) => e.preventDefault()} >{option.label}</div><button className="close btn-close rbt-close rbt-token-remove-button" onClick={() => rTprops.onRemove?.(option)}>×</button>
                              </Reorder.Item>
                              )
                           })}
                        </Reorder.Group>
                     )
                  }}
               />
            </div>

            <div style={{ display: "flex", flexDirection: "column" }}>
               <div
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={(e) => {
                     e.preventDefault();
                     e.stopPropagation();
                     setMode(mode === "reorder" ? "default" : "reorder");
                     return false
                  }}
                  className="changeMode"
                  style={{ cursor: "pointer", padding: "1px 4px" }}
               >
                  <MIBIcon icon="AdjustUpDown" />
               </div>
               {props.settingComponent}
            </div>
         </div>
      </div>
   )
}


interface SelectContextValue<T> {
   activeIndex: number | null;
   selectedIndex: number | null;
   getItemProps: ReturnType<typeof useInteractions>["getItemProps"];
   handleSelect: (value: T) => void;
}

const SelectContext = createContext<SelectContextValue<any>>(
   {} as SelectContextValue<any>
);

export function GlassSelect<T extends string | number | undefined>({ children, value, onSelect, placeholder }: { children: Array<ReactElement<typeof GlassOption<T>>>, value?: T, onSelect?: (value: T) => void, placeholder?: string }) {
   const [isOpen, setIsOpen] = useState(false);
   const [activeIndex, setActiveIndex] = useState<number | null>(null);
   const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
   const [selectedLabel, setSelectedLabel] = useState<ReactNode | null>(null);

   const { refs, floatingStyles, context } = useFloating({
      placement: "bottom-start",
      open: isOpen,
      onOpenChange: setIsOpen,
      whileElementsMounted: autoUpdate,
      middleware: [
         offset(4),
         flip(),
         size({
            apply({ rects, elements }) {
               Object.assign(elements.floating.style, {
                  minWidth: `${rects.reference.width}px`,
               });
            }
         })
      ]
   });

   useEffect(() => {
      if (value === undefined) {
         setSelectedIndex(null);
         setSelectedLabel(null);
      } else {
         const index = React.Children.toArray(children).findIndex((child) => (child as ReactElement).props.value === value);
         setSelectedIndex(index !== -1 ? index : null);
         setSelectedLabel(index !== -1 ? children[index] : null);
      }
   }, [value, children]);

   const elementsRef = React.useRef<Array<HTMLElement | null>>([]);
   const labelsRef = React.useRef<Array<string | null>>([]);

   const handleSelect = React.useCallback((optionValue: T | null | undefined) => {
      let index: number | null = null;
      if (optionValue !== null && optionValue !== undefined) {
         const found = React.Children.toArray(children).findIndex((child) => (child as ReactElement).props.value === optionValue);
         if (found !== -1) index = found;
      }
      setSelectedIndex(index);
      setIsOpen(false);
      if (index !== null) {
         const element = React.Children.toArray(children)[index] as ReactElement;
         if (!element) return;
         onSelect?.(element.props.value);
         setSelectedLabel(element);
      } else {
         onSelect?.(undefined as any);
         setSelectedLabel(null);
      }
   }, [children, onSelect]);

   function handleTypeaheadMatch(index: number | null) {
      if (isOpen) {
         setActiveIndex(index);
      } else {
         if (index === null) {
            handleSelect(null);
         } else {
            const element = React.Children.toArray(children)[index] as ReactElement | undefined;
            handleSelect(element?.props.value ?? null);
         }
      }
   }

   const listNav = useListNavigation(context, {
      listRef: elementsRef,
      activeIndex,
      selectedIndex,
      onNavigate: setActiveIndex
   });
   const typeahead = useTypeahead(context, {
      listRef: labelsRef,
      activeIndex,
      selectedIndex,
      onMatch: handleTypeaheadMatch
   });
   const click = useClick(context);
   const dismiss = useDismiss(context);
   const role = useRole(context, { role: "listbox" });

   const {
      getReferenceProps,
      getFloatingProps,
      getItemProps
   } = useInteractions([listNav, typeahead, click, dismiss, role]);

   const selectContext = React.useMemo(
      () => ({
         activeIndex,
         selectedIndex,
         getItemProps,
         handleSelect
      }),
      [activeIndex, selectedIndex, getItemProps, handleSelect]
   );

   return (
      <>
         <SelectContext.Provider value={{ selectedIndex: -1, activeIndex: null, getItemProps: () => ({}), handleSelect: () => { } }} >
            <div className="GlassSelect" ref={refs.setReference} tabIndex={0} {...getReferenceProps()}>
               {(selectedLabel) ?? <span className="placeholder">{placeholder ?? Localization.getText("Select an option")}</span>}
               <span className="Arrow">
                  <MIBIcon icon="expandDown" />
               </span>
            </div>
         </SelectContext.Provider>
         <SelectContext.Provider value={selectContext}>
            {isOpen && (
               <MITFloatingPortal>
                  <FloatingFocusManager context={context} modal={false} >
                     <div
                        className="GlassSelect-options"
                        ref={refs.setFloating}
                        style={{ ...floatingStyles, maxHeight: "200px", overflowY: "auto", padding: "2px" }}
                        {...getFloatingProps()}
                     >
                        <FloatingList elementsRef={elementsRef} labelsRef={labelsRef}>
                           {children}
                        </FloatingList>
                     </div>
                  </FloatingFocusManager>
               </MITFloatingPortal>
            )}
         </SelectContext.Provider>
      </>
   );
}

export function GlassOption<T extends string | number | undefined>({ value, label, disabled, children, ...props }: { value: T, label?: string, children?: ReactNode, disabled?: boolean } & React.HTMLAttributes<HTMLButtonElement>) {
   const {
      activeIndex,
      selectedIndex,
      getItemProps,
      handleSelect
   } = React.useContext(SelectContext);

   const { ref, index } = useListItem({ label: label ?? String(value) });

   const isActive = activeIndex === index;
   const isSelected = selectedIndex === index;
   const isDisabled = disabled;

   return (
      <button
         ref={ref}
         role="option"
         aria-disabled={isDisabled}
         aria-selected={isActive && isSelected}
         tabIndex={isActive ? 0 : -1}
         {...getItemProps({
            onClick: () => handleSelect(value)
         })}
         {...props}
         disabled={isDisabled}
         onKeyDown={(e) => {
            if (e.key === "Enter") {
               e.preventDefault();
               handleSelect(value);
            }
         }}
         className={
            "GlassOption " +
            (isActive ? "active " : "") +
            (isSelected ? "selected " : "") +
            (props.className || "")
         }
      >
         {children ?? label ?? String(value)}
      </button>
   );
}

function LayerOption({ layer, label, value }: { layer: LayerInfo, label?: string, value?: number }): ReactElement<typeof GlassOption<number>> {
   return <GlassOption label={label} className="LayerOption" value={value}>
      <span className="icon" style={{ color: layer?.styling?.color ?? "inherit" }}>
         {iconForLayerType(layer?.type)}
      </span>
      <span className="label">
         {layer?.datasetname}
      </span>
   </GlassOption>
}

export function GlassLayerSelect(props: {
   selected: number | undefined,
   onSelect: (layerId: number) => void,
   layers: LayerInfo[]
}) {

   return (
      <GlassInputControl>
         <GlassSelect value={props.selected} onSelect={props.onSelect}
            placeholder={Localization.getText("Select layer...")}
         >
            {props.layers.map((layer) =>
               <LayerOption key={layer.layerId} layer={layer} label={layer.datasetname} value={layer.layerId} />
            )}
         </GlassSelect>
      </GlassInputControl>
   );
}

function LayerGroupOption({ label, value, works, allLayers }: { label?: string, value?: number, works?: number, allLayers?: number[] }): ReactElement<typeof GlassOption<number>> {
   return <GlassOption label={label} className="LayerOption" value={value}>
      <span className="icon" style={{ color: "inherit" }}>
         {allLayers?.length ?
            <MIBIcon icon="layerGroup" />
            : <MIBIcon icon="emptyGroup" />
         }
      </span>
      <span className="label">
         {label}
      </span>
      <span style={{ marginLeft: "auto", fontSize: "0.6em" }}>{works}/{allLayers?.length}</span>
   </GlassOption>
}

export function GlassLayerGroupSelect(props: {
   selected: number | undefined,
   onSelect: (groupId: number, layerId: number[]) => void,
   layerHierarchy: LayerHierarchy,
   layers: LayerInfo[]
}) {

   let selectableGroups: { name: string, id: number, workingLayer: number, layers: number[] }[] = useMemo(() => {
      let allGroups = getAllGroupsAndDecendedLayers(props.layerHierarchy);
      let workingLayerIds = new Set(props.layers.filter((a) => a?.visible).map(layer => layer.layerId));
      return allGroups.map((group) => ({
         ...group,
         name: group.name === "Top Level" ? Localization.getText("All Layers") : group.name,
         workingLayer: group.layers.filter((a) => workingLayerIds.has(a)).length
      }));
   }, [props.layerHierarchy, props.layers]);

   useEffect(() => {
      let group = selectableGroups.find(g => g.id === props.selected);
      if (group) {
         props.onSelect(group.id, group.layers);
      } else {
         props.onSelect(selectableGroups.at(0)?.id ?? 0, selectableGroups.at(0)?.layers ?? []);
      }
   }, [selectableGroups, props.layers])

   function onFolderSelect(selectedLayerGroupId: number) {
      let group = selectableGroups.find(g => g.id === selectedLayerGroupId);
      if (group) {
         props.onSelect(group.id, group.layers);
      } else {
         props.onSelect(selectableGroups.at(0)?.id ?? 0, selectableGroups.at(0)?.layers ?? []);
      }
   }

   return (
      <GlassInputControl>
         <GlassSelect value={props.selected} onSelect={(val: number) => onFolderSelect(val)}
            placeholder={Localization.getText("Select layer...")}
         >
            {selectableGroups.map((layer) =>
               <LayerGroupOption key={layer.id} label={layer.name} value={layer.id} works={layer.workingLayer} allLayers={layer.layers} />
            )}
         </GlassSelect>
      </GlassInputControl>
   );
}

function GlassMultiSelect<T extends string | number | undefined>({ children, value, onSelect, placeholder }: { children: Array<ReactElement<typeof GlassOption<T>>>, value?: T[], onSelect?: (value: T[]) => void, placeholder?: string }) {
   const [isOpen, setIsOpen] = useState(false);
   const [activeIndex, setActiveIndex] = useState<number | null>(null);
   const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
   const [selectedLabel, setSelectedLabel] = useState<ReactNode[]>([]);

   const { refs, floatingStyles, context } = useFloating({
      placement: "bottom-start",
      open: isOpen,
      onOpenChange: setIsOpen,
      whileElementsMounted: autoUpdate,
      middleware: [
         offset(4),
         flip(),
         size({
            apply({ rects, elements }) {
               Object.assign(elements.floating.style, {
                  minWidth: `${rects.reference.width}px`,
               });
            }
         })
      ]
   });

   useEffect(() => {
      if (value === undefined) {
         setSelectedIndex(null);
         setSelectedLabel([]);
      } else {
         let indexes = value.map(v => React.Children.toArray(children).findIndex((child) => (child as ReactElement).props.value === v))
         setSelectedIndex(indexes.length > 0 ? indexes[0] : null);
         setSelectedLabel(indexes.map(i => i !== -1 ? children[i] : null).filter(a => a !== null) as ReactNode[]);
      }
   }, [value, children]);

   const elementsRef = React.useRef<Array<HTMLElement | null>>([]);
   const labelsRef = React.useRef<Array<string | null>>([]);

   const handleSelect = React.useCallback((optionValue: any) => {
      let index = React.Children.toArray(children).findIndex((child) => (child as ReactElement).props.value === optionValue);
      setSelectedIndex(index);
      // setIsOpen(false);
      if (index !== null) {
         const element = React.Children.toArray(children)[index] as ReactElement;
         if (!element) return;
         let selectedValue = [...(value || []), optionValue];
         console.log("Selected value:", selectedValue);
         onSelect?.(selectedValue);
         setSelectedLabel([element]);
      }
   }, [children]);

   const handleDeselect = React.useCallback((optionValue: any) => {
      let index = React.Children.toArray(children).findIndex((child) => (child as ReactElement).props.value === optionValue);
      if (index === -1) return;
      let selectedValue = (value || []).filter(v => v !== optionValue);
      onSelect?.(selectedValue);
      setSelectedLabel(selectedLabel.filter(label => (label as ReactElement).props.value !== optionValue));
   }, [children]);

   function handleTypeaheadMatch(index: number | null) {
      if (isOpen) {
         setActiveIndex(index);
      } else {
         // handleSelect(index);
      }
   }

   const listNav = useListNavigation(context, {
      listRef: elementsRef,
      activeIndex,
      selectedIndex,
      onNavigate: setActiveIndex
   });
   const typeahead = useTypeahead(context, {
      listRef: labelsRef,
      activeIndex,
      selectedIndex,
      onMatch: handleTypeaheadMatch
   });
   const click = useClick(context);
   const dismiss = useDismiss(context);
   const role = useRole(context, { role: "listbox" });

   const {
      getReferenceProps,
      getFloatingProps,
      getItemProps
   } = useInteractions([listNav, typeahead, click, dismiss, role]);

   const selectContext = React.useMemo(
      () => ({
         activeIndex,
         selectedIndex,
         getItemProps,
         handleSelect
      }),
      [activeIndex, selectedIndex, getItemProps, handleSelect]
   );

   const availableOptions = React.Children.toArray(children).filter((child) => {
      const childValue = (child as ReactElement).props.value;
      return !value?.includes(childValue);
   });

   return (
      <>
         <SelectContext.Provider value={{ selectedIndex: -1, activeIndex: null, getItemProps: () => ({}), handleSelect: (e) => { handleDeselect(e) } }} >
            <div className="GlassSelect Multi" ref={refs.setReference} tabIndex={0} {...getReferenceProps()}>
               {(selectedLabel.length > 0 ? selectedLabel : <span className="placeholder">{placeholder ?? Localization.getText("Select an option")}</span>)}
               <span className="Arrow">
                  <MIBIcon icon="expandDown" />
               </span>
            </div>
         </SelectContext.Provider>
         <SelectContext.Provider value={selectContext}>
            {isOpen && (
               <MITFloatingPortal>
                  <FloatingFocusManager context={context} modal={false} >
                     <div
                        className="GlassSelect-options"
                        ref={refs.setFloating}
                        style={floatingStyles}
                        {...getFloatingProps()}
                     >
                        <FloatingList elementsRef={elementsRef} labelsRef={labelsRef}>
                           {availableOptions.length ? availableOptions : <div className="GlassOption">{Localization.getText("No more options")}</div>}
                        </FloatingList>
                     </div>
                  </FloatingFocusManager>
               </MITFloatingPortal>
            )}
         </SelectContext.Provider>
      </>
   );
}


export function GlassMultiLayerSelect(props: {
   selected: number[] | undefined,
   onSelect: (layerId: number[]) => void,
   layers: LayerInfo[]
}) {



   return (
      <GlassInputControl>
         <GlassMultiSelect value={props.selected} onSelect={props.onSelect}
            placeholder={Localization.getText("Select layer(s)...")}
         >
            {props.layers.map((layer) =>
               <LayerOption key={layer.layerId} layer={layer} label={layer.datasetname} value={layer.layerId} />
            )}
         </GlassMultiSelect>
      </GlassInputControl>
   )
}

export function SelectedLayers(props: {
   value: number[] | undefined,
   onChange: (layerId: number[]) => void,
   layers: LayerInfo[]
}) {
   return (
      <div className="SelectedLayers">
         {(props.value || []).map((id) => {
            let layer = props.layers.find((l) => l.layerId === id);
            if (!layer) return null;
            return (<CompactedLayerView key={id} layer={layer} onRemove={(e: React.MouseEvent<HTMLSpanElement>) => {
               e.stopPropagation();
               props.onChange((props.value || []).filter((i) => i !== id))
            }} />)
         })}
      </div>
   )
}

export function CompactedLayerView(props: {
   layer: LayerInfo,
   onRemove: (e: React.MouseEvent<HTMLSpanElement>) => void
}) {
   return (
      <div className="CompactedLayerView">
         <span className="icon" style={{ color: props.layer?.styling?.color ?? "inherit" }}>
            {iconForLayerType(props.layer?.type)}
         </span>
         <span className="remove" onClick={props.onRemove}>
            <MIBIcon icon="close" />
         </span>
      </div>
   )
}

function iconForLayerType(type: LayerType) {
   switch (type) {
      case LayerType.Point:
      case LayerType.PointWGS84:
      case LayerType.GeoJSON_Point:
      case LayerType.PointUTM32: {
         return <MIBIcon icon="point" />
      }
      case LayerType.Area:
      case LayerType.AreaMunicipality:
      case LayerType.AreaRegion:
      case LayerType.AreaZipcodes:
      case LayerType.AreaMunicipalityId:
      case LayerType.AreaRegionId:
      case LayerType.AreaZipcodesId:
      case LayerType.AreaMunicipalityName:
      case LayerType.AreaRegionName:
      case LayerType.AreaZipcodesName:
      case LayerType.AreaCountry:
      case LayerType.AreaCountryName:
      case LayerType.AreaCountryId:
      case LayerType.GeoCode:
      case LayerType.GeoJSON:
      case LayerType.GeoJSON_Polygon:
      case LayerType.GeoJSON_Line: {
         return <MIBIcon icon="polygon" />
      }
      case LayerType.AreaMunicipality_RO:
      case LayerType.AreaMunicipalityId_RO:
      case LayerType.AreaMunicipalityName_RO:
      case LayerType.AreaMunicipality_DK_And_SydSlesvig:
      case LayerType.AreaMunicipalityId_DK_And_SydSlesvig:
      case LayerType.AreaMunicipalityName_DK_And_SydSlesvig:
      case LayerType.AreaParish:
      case LayerType.AreaParishId:
      case LayerType.AreaParishName:
      case LayerType.Grid:
      case LayerType.PointCadaster:
      case LayerType.AreaCadaster:
      case LayerType.AreaProperty:
      case LayerType.AreaSFE:
      case LayerType.ImageOverlay:
      default: {
         return <MIBIcon icon="question" />
      }
   }
};